import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import api from '../axiosConfig'
import DOMPurify from 'dompurify';

// Permite refrescar las notificaciones desde otros componentes
// Ejemplo de uso: import { requestNotificationsRefresh } from './NotificaionGui';
// y luego llamar requestNotificationsRefresh() al crear un Job de descarga.
export function requestNotificationsRefresh() {
  document.dispatchEvent(new CustomEvent('notifications:refresh'));
}

const CACHE_KEY = "notif_cache_v1";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Util: fecha relativa simple
const relativeDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMs = now - d;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days <= 0) return "hoy";
    if (days === 1) return "ayer";
    return `hace ${days} días`;
};

// Util: normaliza URL (agrega https:// si falta)
const normalizeUrl = (url) => {
    if (!url) return "#";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};


/**
 * Convierte un string HTML a texto plano
 * @param {string} html - Contenido HTML
 * @returns {string} Texto plano
 */
function htmlToPlainText(html) {
  if (!html) return "";

  // Crear un contenedor temporal
  const temp = document.createElement("div");
  temp.innerHTML = html;

  // Obtener texto sin etiquetas
  const text = temp.textContent || temp.innerText || "";

  // Limpiar espacios extras
  return text.trim();
}

const NotificationDropdown = ({ dominio, authToken }) => {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const dropdownRef = useRef(null);

    // ---- NUEVO: control para evitar overlaps y para el intervalo
    const fetchingRef = useRef(false);
    const intervalRef = useRef(null);

    const loadFromCache = () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return false;
        const { ts, items: cached } = JSON.parse(raw);
        if (!ts || !Array.isArray(cached)) return false;
        const fresh = Date.now() - ts < CACHE_TTL_MS;
        if (!fresh) return false;
        setItems(cached);
        setLoaded(true);
        setLoading(false);
        return true;
      } catch {
        return false;
      }
    };

    const saveToCache = (list) => {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: list }));
      } catch {}
    };

    // Compara dos notificaciones por campos relevantes (ignora referencias/funciones)
    const eqNotif = (a, b) => {
      if (!a || !b) return false;
      return a.idNotificacion === b.idNotificacion
        && a.titulo === b.titulo
        && a.mensaje === b.mensaje
        && a.fecha === b.fecha
        && a.s3 === b.s3
        && a.enlace === b.enlace
        && ((a.idTipoNotificacion?.tipo) === (b.idTipoNotificacion?.tipo));
    };

    // Determina si la lista cambió en orden, cantidad o contenido editable
    const listChanged = (prev, next) => {
      if (!Array.isArray(prev) || !Array.isArray(next)) return true;
      if (prev.length !== next.length) return true;
      for (let i = 0; i < next.length; i++) {
        const p = prev[i];
        const n = next[i];
        if (!eqNotif(p, n)) return true;
      }
      return false;
    };

    const fetchNotificaciones = async (force = false) => {
      if (!force) {
        const used = loadFromCache();
        if (used) return;
      }

      if (fetchingRef.current) return; // evita llamadas simultáneas
      fetchingRef.current = true;
      setErr((prev) => (prev ? "" : prev));

      if (!loaded && items.length === 0) setLoading(true);

      try {
        const res = await api.get(`/notificaciones/all`);
        if (res.status === 200) {
          const data = Array.isArray(res.data) ? res.data : [];
          setItems((prev) => {
            const changed = listChanged(prev, data);
            if (changed) {
              saveToCache(data);
              return data;
            }
            return prev;
          });
          setLoaded(true);
        }
      } catch (e) {
        setErr("No se pudieron cargar las notificaciones.");
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    };

    useEffect(() => {
      // Listener global para refrescar desde otros componentes
      const onRefresh = () => {
        fetchNotificaciones(true);
      };
      document.addEventListener('notifications:refresh', onRefresh);

      // Carga inicial: intenta usar cache, si no hay, pega a la API
      const hadCache = loadFromCache();
      if (!hadCache) {
        fetchNotificaciones(true); // primera carga desde API si no hay cache fresco
      }

      // Polling estricto cada 5 minutos
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        fetchNotificaciones(true); // siempre refresca desde API cada 5 min
      }, 5 * 60 * 1000);

      return () => {
        document.removeEventListener('notifications:refresh', onRefresh);
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [dominio, authToken]);

    const countStr = useMemo(() => String(items.length).padStart(2, "0"), [items.length]);

    // Abre modal con la notificación seleccionada
    const openModal = async (n) => {
        setSelected(n);
        setOpen(true);

        // registramos que vio la notificacion
        const res = await api.post(`/notificaciones/vista/${n.idNotificacion}`);
      if (res.status === 200) {

      }


    };

     const sanitize = (html) => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

    const closeModal = () => {
        setOpen(false);
        setSelected(null);
    };

    return (
        <>
            {/* Dropdown */}
            <div className="dropdown" ref={dropdownRef}>
                <button
                    className="has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center position-relative"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    <Icon icon="iconoir:bell" className="text-primary-light text-xl" />
                    {items.length > 0 && (
                        <span
                            className="position-absolute translate-middle p-1 bg-danger border border-light rounded-circle"
                            style={{ top: 6, right: 6 }}
                        />
                    )}
                </button>

                <div className="dropdown-menu to-top dropdown-menu-lg p-0">
                    <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                        <div>
                            <h6 className="text-lg text-neutral-50 fw-semibold mb-0">Notificaciones</h6>
                        </div>
                        <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
                            {countStr}
                        </span>
                    </div>

                    <div className="max-h-400-px overflow-y-auto scroll-sm pe-4">
                        {loading && <div className="px-24 py-12 text-secondary-light">Cargando…</div>}

                        {err && !loading && <div className="px-24 py-12 text-danger">{err}</div>}

                        {!loading && !err && items.length === 0 && (
                            <div className="px-24 py-12 text-secondary-light">Sin notificaciones.</div>
                        )}

                        {!loading &&
                            !err &&
                            items.map((n) => {
                                const fechaRel = relativeDate(n?.fecha);
                                const img = n?.s3;

                                return (
                                    <button
                                        key={n.idNotificacion}
                                        type="button"
                                        onClick={() => openModal(n)}
                                        className="w-100 text-start bg-transparent border-0 px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between hover-bg-neutral-100"
                                        style={{ cursor: "pointer" }}
                                    >
                                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                                            <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0 overflow-hidden">
                                                {img ? (
                                                    <img
                                                        src={img}
                                                        alt=""
                                                        style={{ width: 44, height: 44, objectFit: "cover" }}
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = "none";
                                                            e.currentTarget.parentElement.innerHTML =
                                                                '<span class="d-flex align-items-center justify-content-center w-44-px h-44-px"><svg width="22" height="22" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2m1 15h-2v-2h2zm0-4h-2V7h2z"/></svg></span>';
                                                        }}
                                                    />
                                                ) : (
                                                    <Icon icon="bitcoin-icons:verify-outline" className="icon text-xxl" />
                                                )}
                                            </span>
                                            <div>
                                                <h6 className="text-md fw-semibold mb-4">{n.titulo || "Notificación"}</h6>
                                                <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                                                    { htmlToPlainText(n.mensaje)  } 
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-sm text-secondary-light flex-shrink-0">{fechaRel}</span>
                                    </button>
                                );
                            })}
                    </div>

                    <div className="text-center py-12 px-16">
                        {/* espacio para "Ver todas" */}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {open && selected && (
                <div
                    className="modal show d-block"
                    tabIndex="-1"
                    role="dialog"
                    aria-modal="true"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content radius-12">
                            <div className="modal-header">
                                <h5 className="modal-title mb-0">{selected.titulo || "Detalle de notificación"}</h5>
                                <button type="button" className="btn-close" onClick={closeModal} />
                            </div>

                            <div className="modal-body">
                                {/* Imagen S3 */}
                                {selected.s3 && (
                                    <div className="mb-3 text-center">
                                        <img
                                            src={selected.s3}
                                            alt=""
                                            className="img-fluid rounded"
                                            style={{ maxHeight: 280, objectFit: "cover" }}
                                            onError={(e) => (e.currentTarget.style.display = "none")}
                                        />
                                    </div>
                                )}

                                {/* Texto */}
                                {selected.mensaje && ( 
                                    <div className="mb-20" dangerouslySetInnerHTML={{ __html: sanitize( selected.mensaje ) }} />
                                )}

                                {/* Fecha y tipo */}
                                <div className="d-flex gap-3 flex-wrap">
                                    {selected.fecha && (
                                        <span className="badge bg-neutral-200 text-secondary-light">
                                            {relativeDate(selected.fecha)}
                                        </span>
                                    )}
                                    {selected.idTipoNotificacion?.tipo && (
                                        <span className="badge bg-primary-50 text-white">
                                            {selected.idTipoNotificacion.tipo}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">


                                {/* Ver más */}
                                {selected.enlace && (
                                    <a
                                        className="btn btn-primary"
                                        href={normalizeUrl(selected.enlace)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        role="button"
                                    >
                                        Ver más
                                    </a>
                                )}


                                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NotificationDropdown;